import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { AED, fmtAdmin, fmtDateTime, maskIban } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserEmails } from "@/components/admin/AdminEmails";
import { LegalSnapshotDialog } from "@/components/admin/LegalSnapshot";
import { fmtBal, depositStateLabel, depositStateColor } from "@/lib/statusLabels";
import { DubaiAreaSelect } from "@/components/DubaiAreaSelect";
import { ArrowLeft, Mail, Phone, MapPin, Calendar, ShieldCheck, Hash, Pencil } from "lucide-react";

const fmtDT = fmtDateTime;
const fmtNote = (n) => String(n || "").replace(/\d{4}-\d{2}-\d{2}T[\d:.]+(?:\+00:00|Z)?/g, (m) => fmtDateTime(m));
const imgSrc = (s) => (!s ? "" : s.startsWith("http") || s.startsWith("data:") || s.startsWith("/") ? s : `data:image/jpeg;base64,${s}`);
const KYC_CLS = {
  not_submitted: "bg-slate-100 text-slate-600",
  under_review: "bg-blue-100 text-blue-700",
  verified: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700",
};
const KYC_LABEL = { not_submitted: "Not submitted", under_review: "Under review", verified: "Verified", rejected: "Rejected" };

const cap = (s) => (s || "").toString().replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
const DISPUTE_LABELS = { open: "Open", pending: "Pending", resolved_buyer: "Resolved — buyer refunded", resolved_seller: "Resolved — sale stands", resolved: "Resolved", closed: "Closed" };
const LISTING_LABELS = { active: "Active", sold: "Sold", draft: "Draft", hidden: "Hidden", removed: "Removed", locked: "Reserved", pending_review: "Pending review" };
const TXN_LABELS = { ESCROW_HELD: "Payment held", COMPLETED: "Completed", DISPUTED: "Disputed", CANCELLED: "Cancelled", REFUNDED: "Refunded", PENDING: "Pending", FAILED: "Failed" };
const dLabel = (s) => DISPUTE_LABELS[s] || cap(s);
const lLabel = (s) => LISTING_LABELS[s] || cap(s);
const tLabel = (s) => TXN_LABELS[s] || cap(s);

export default function AdminUserProfile({ userId, onBack }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [snapV, setSnapV] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [kycReject, setKycReject] = useState(false);
  const [kycReason, setKycReason] = useState("");
  const [verifyName, setVerifyName] = useState("");
  const [sc, setSc] = useState({ un_scl: false, uae_ltl: false, pep_clear: false, adverse_clear: false });
  const [scNotes, setScNotes] = useState("");
  const [enlarge, setEnlarge] = useState(null);
  const [banOpen, setBanOpen] = useState(false);
  const [banReason, setBanReason] = useState("");
  const [unbanOpen, setUnbanOpen] = useState(false);
  const [unbanReason, setUnbanReason] = useState("");
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState("");
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState("");
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreReason, setRestoreReason] = useState("");
  const [showAllLedger, setShowAllLedger] = useState(false);

  const load = () => api.get(`/admin/users/${userId}`).then(({ data }) => setData(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const openEdit = () => {
    setForm({ name: u.name || "", mobile: u.mobile || "", emirate: u.emirate || "", area: u.area || "", full_address: u.full_address || "", building_access: u.building_access || "" });
    setEditOpen(true);
  };
  const setF = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const saveEdit = async () => {
    setSaving(true);
    try { await api.post(`/admin/users/${userId}/edit`, form); toast.success("Profile updated"); setEditOpen(false); load(); }
    catch { toast.error("Could not update profile"); }
    finally { setSaving(false); }
  };

  const doAction = async () => {
    try { await api.post(`/admin/users/${userId}/${confirm[0]}`); toast.success(`${confirm[1]} done`); setConfirm(null); load(); }
    catch { toast.error("Action failed"); }
  };

  const doBan = async () => {
    if (!banReason.trim()) return;
    try { await api.post(`/admin/users/${userId}/ban`, { reason: banReason.trim() }); toast.success("Account permanently closed"); setBanOpen(false); setBanReason(""); load(); }
    catch { toast.error("Could not ban account"); }
  };
  const doUnban = async () => {
    if (!unbanReason.trim()) return;
    try { await api.post(`/admin/users/${userId}/unban`, { reason: unbanReason.trim() }); toast.success("Ban reversed"); setUnbanOpen(false); setUnbanReason(""); load(); }
    catch { toast.error("Could not reverse ban"); }
  };
  const doSuspend = async () => {
    if (!suspendReason.trim()) return;
    try { await api.post(`/admin/users/${userId}/suspend`, { reason: suspendReason.trim() }); toast.success("Account suspended"); setSuspendOpen(false); setSuspendReason(""); load(); }
    catch { toast.error("Could not suspend"); }
  };
  const doArchive = async () => {
    if (!archiveReason.trim()) return;
    try { await api.post(`/admin/users/${userId}/archive`, { reason: archiveReason.trim() }); toast.success("User archived"); setArchiveOpen(false); setArchiveReason(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not archive user"); }
  };
  const doRestore = async () => {
    try { await api.post(`/admin/users/${userId}/restore`, { reason: restoreReason.trim() }); toast.success("User restored"); setRestoreOpen(false); setRestoreReason(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not restore user"); }
  };

  const approveKyc = async () => {
    if (!verifyName.trim()) { toast.error("Enter the full name exactly as printed on the Emirates ID"); return; }
    if (!(sc.un_scl && sc.uae_ltl && sc.pep_clear && sc.adverse_clear)) { toast.error("Complete all four screening checks before approving"); return; }
    try { await api.post(`/admin/kyc/${userId}/approve`, { verified_name: verifyName.trim(), ...sc, screening_notes: scNotes.trim(), outcome: "no_match" }); toast.success("Identity verified"); setVerifyName(""); setScNotes(""); setSc({ un_scl: false, uae_ltl: false, pep_clear: false, adverse_clear: false }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not approve"); }
  };
  const matchKyc = async () => {
    if (!window.confirm("Record a screening MATCH? This blocks all payouts & deposit refunds and suspends the seller (Compliance review).")) return;
    try { await api.post(`/admin/kyc/${userId}/approve`, { ...sc, screening_notes: scNotes.trim(), outcome: "match" }); toast.success("Match recorded — seller suspended"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not record"); }
  };
  const reScreen = async (outcome) => {
    if (outcome === "no_match" && !(sc.un_scl && sc.uae_ltl && sc.pep_clear && sc.adverse_clear)) { toast.error("Complete all four screening checks"); return; }
    if (outcome === "match" && !window.confirm("Record a screening MATCH? This blocks payouts/refunds and suspends the seller.")) return;
    try { await api.post(`/admin/users/${userId}/screen`, { ...sc, notes: scNotes.trim(), outcome }); toast.success(outcome === "match" ? "Match recorded" : "Re-screening recorded"); setScNotes(""); setSc({ un_scl: false, uae_ltl: false, pep_clear: false, adverse_clear: false }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not record screening"); }
  };
  const rejectKyc = async () => {
    try { await api.post(`/admin/kyc/${userId}/reject`, { reason: kycReason }); toast.success("Identity rejected"); setKycReject(false); load(); }
    catch { toast.error("Could not reject"); }
  };

  if (!data) return <div className="py-20 text-center text-slate-400">Loading…</div>;
  const u = data.user;
  const kyc = u.kyc || { status: u.kyc_status || "not_submitted" };
  const statusBadge = u.is_archived ? <Badge className="rounded-full bg-slate-200 text-slate-600">Archived</Badge>
    : u.is_banned ? <Badge className="rounded-full bg-red-100 text-red-700">Banned</Badge>
    : u.is_suspended ? <Badge className="rounded-full bg-amber-100 text-amber-700">Suspended</Badge>
    : <Badge className="rounded-full bg-emerald-100 text-emerald-700">Active</Badge>;

  return (
    <div data-testid="admin-user-profile">
      <button onClick={onBack} className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="profile-back"><ArrowLeft className="h-4 w-4" /> Back to users</button>

      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 font-heading text-2xl font-bold text-primary">{u.name?.[0]}</div>
            <div>
              <h2 className="font-heading text-2xl font-bold text-slate-900">{u.name}</h2>
              {u.user_id && <p className="mt-0.5 flex items-center gap-1 font-mono text-xs text-slate-400" data-testid="admin-profile-user-id"><Hash className="h-3 w-3" />{u.user_id}</p>}
              <div className="mt-1 flex items-center gap-2">{statusBadge}<Badge className={`rounded-full ${depositStateColor(u)}`} data-testid="admin-deposit-state-badge">{depositStateLabel(u)}</Badge>{(u.balance_owed ?? 0) > 0 && <Badge className="rounded-full bg-red-100 text-red-700" data-testid="admin-balance-owed-badge">Owes AED {Math.round((u.balance_owed || 0) * 100) / 100}</Badge>}</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" className="rounded-full bg-primary" data-testid="profile-edit-open" onClick={openEdit}><Pencil className="mr-1 h-4 w-4" /> Edit Profile</Button>
            {u.is_archived ? (
              <Button size="sm" variant="outline" className="rounded-full border-primary/40 text-primary hover:bg-primary/5" data-testid="profile-action-restore" onClick={() => { setRestoreReason(""); setRestoreOpen(true); }}>Restore user…</Button>
            ) : u.is_banned ? (
              <>
                <Button size="sm" variant="outline" className="rounded-full border-red-300 text-red-700 hover:bg-red-50" data-testid="profile-action-unban" onClick={() => { setUnbanReason(""); setUnbanOpen(true); }}>Reverse ban…</Button>
                <Button size="sm" variant="outline" className="rounded-full" data-testid="profile-action-archive" onClick={() => { setArchiveReason(""); setArchiveOpen(true); }}>Archive…</Button>
              </>
            ) : (
              <>
                <Button size="sm" variant="outline" className="rounded-full" data-testid="profile-action-warn" onClick={() => setConfirm(["warn", "Warn"])}>Warn</Button>
                {u.is_suspended
                  ? <Button size="sm" variant="outline" className="rounded-full" data-testid="profile-action-unsuspend" onClick={() => setConfirm(["unsuspend", "Unsuspend"])}>Unsuspend</Button>
                  : <Button size="sm" variant="outline" className="rounded-full" data-testid="profile-action-suspend" onClick={() => { setSuspendReason(""); setSuspendOpen(true); }}>Suspend</Button>}
                <Button size="sm" className="rounded-full bg-red-600 hover:bg-red-700" data-testid="profile-action-ban" onClick={() => { setBanReason(""); setBanOpen(true); }}>Ban</Button>
                <Button size="sm" variant="outline" className="rounded-full" data-testid="profile-action-archive" onClick={() => { setArchiveReason(""); setArchiveOpen(true); }}>Archive…</Button>
              </>
            )}
          </div>
        </div>

        {u.is_suspended && !u.is_banned && (
          <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="admin-suspended-banner">
            <p className="text-sm font-bold text-amber-800">Account suspended</p>
            <p className="mt-1 text-sm text-amber-800" data-testid="admin-suspend-reason">Reason: {u.suspend_reason || "No reason recorded"}</p>
            <p className="mt-0.5 text-xs text-amber-700" data-testid="admin-suspend-date">Suspended on {u.suspended_at ? fmtDateTime(u.suspended_at) : "—"}</p>
          </div>
        )}

        {u.is_banned && (
          <div className="mt-4 rounded-xl border-2 border-red-400 bg-red-50 p-4" data-testid="admin-banned-banner">
            <p className="text-sm font-bold uppercase tracking-wide text-red-700">⛔ Account permanently closed (banned)</p>
            <p className="mt-1 text-sm text-red-800" data-testid="admin-ban-reason">Reason: {u.ban_reason || "No reason recorded"}</p>
            <p className="mt-0.5 text-xs text-red-600" data-testid="admin-ban-date">Banned on {u.banned_at ? fmtDateTime(u.banned_at) : "—"}</p>
          </div>
        )}

        {u.is_archived && (
          <div className="mt-4 rounded-xl border border-slate-300 bg-slate-50 p-4" data-testid="admin-archived-banner">
            <p className="text-sm font-bold text-slate-700">Account archived</p>
            <p className="mt-1 text-sm text-slate-700" data-testid="admin-archive-reason">Reason: {u.archive_reason || "No reason recorded"}</p>
            <p className="mt-0.5 text-xs text-slate-500" data-testid="admin-archive-date">Archived on {u.archived_at ? fmtDateTime(u.archived_at) : "—"}</p>
            <p className="mt-1 text-xs text-slate-500">Hidden from the Users list, excluded from dashboard counts and blocked from logging in. All orders, deposits, payouts, Emirates ID, screening and audit records are kept.</p>
          </div>
        )}

        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <Info icon={Mail} label="Email" value={u.email} />
          <Info icon={Phone} label="Phone" value={u.mobile} />
          <Info icon={MapPin} label="Emirate / Area" value={`${u.emirate || "—"} · ${u.area || "—"}`} />
          <Info icon={MapPin} label="Full address" value={u.full_address || "—"} />
          <Info icon={ShieldCheck} label="Building access" value={u.building_access || "—"} />
          <Info icon={Calendar} label="Registered" value={fmtDateTime(u.created_at)} />
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Metric label="Listings" value={u.listings_count} />
          <Metric label="Sales" value={u.sales_count} />
          <Metric label="Purchases" value={u.purchases_count} />
          <Metric label="Disputes" value={u.disputes_count} />
        </div>

        {(u.seller_missed_slots >= 3 || u.seller_failed_collections >= 3) && (
          <div className="mt-4 rounded-xl border border-red-300 bg-red-50 p-3 text-sm font-semibold text-red-700" data-testid="seller-poor-record-banner">
            This seller has a poor response record — consider suspension.
          </div>
        )}
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="seller-performance">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Seller performance</p>
          <div className="grid gap-2 text-sm text-slate-700 sm:grid-cols-3">
            <p data-testid="perf-missed-slots">Missed slot cancellations: <b>{u.seller_missed_slots ?? 0}</b></p>
            <p data-testid="perf-failed-collections">Failed collections: <b>{u.seller_failed_collections ?? 0}</b></p>
            <p data-testid="perf-avg-response">Average slot response time: <b>{u.seller_avg_slot_response_hours != null ? `${u.seller_avg_slot_response_hours} hours` : "—"}</b></p>
          </div>
        </div>

        {Array.isArray(u.admin_notes) && u.admin_notes.length > 0 && (
          <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="admin-audit-notes">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-amber-700">Admin audit log</p>
            <ul className="space-y-1">{u.admin_notes.slice().reverse().map((n, i) => <li key={i} className="text-xs text-amber-800">• {fmtNote(n)}</li>)}</ul>
          </div>
        )}
      </div>

      <Section title={`Listings (${data.listings.length})`}>
        {data.listings.length === 0 ? <Empty /> : data.listings.map((l) => (
          <Line key={l.id} left={<button onClick={() => navigate(`/listing/${l.id}`)} className="text-left text-primary hover:underline" data-testid="listing-title-link">{l.title}</button>} right={<Badge className="rounded-full bg-slate-100 text-slate-700">{lLabel(l.status)}</Badge>} sub={l.listing_type === "free" ? "Free" : AED(l.listing_price)} />
        ))}
      </Section>

      <Section title="Security deposit">
        <div className="rounded-xl bg-slate-50 p-3" data-testid="admin-deposit-section">
          <p className="text-sm text-slate-700">Balance: <b data-testid="admin-deposit-balance" className={(u.deposit_balance ?? 0) < 0 ? "text-red-600" : ""}>{fmtBal(u.deposit_balance ?? 0)}</b> · Status: <span data-testid="admin-deposit-state">{depositStateLabel(u)}</span></p>
          {(u.balance_owed ?? 0) > 0 && <p className="mt-1 text-sm font-semibold text-red-700" data-testid="admin-balance-owed">Outstanding balance owed: AED {Math.round((u.balance_owed || 0) * 100) / 100} (blocks buying &amp; claiming until settled)</p>}
          {Array.isArray(u.deposit_ledger) && u.deposit_ledger.length > 0 && (() => {
            const all = u.deposit_ledger.slice().reverse();
            const rows = showAllLedger ? all : all.slice(0, 5);
            return (
            <div className="mt-2 space-y-1">
              {rows.map((e, i) => (
                <div key={i} className="flex items-center justify-between text-xs text-slate-500" data-testid="admin-deposit-ledger-row">
                  <span>{fmtDateTime(e.date)} · {e.description}{e.order_ref ? ` · CR-${String(e.order_ref).slice(0, 8).toUpperCase()}` : ""}</span>
                  <span className={e.amount < 0 ? "text-red-600" : "text-emerald-700"}>{e.amount < 0 ? "−" : "+"}AED {Math.abs(e.amount)} (bal {fmtBal(e.balance_after)})</span>
                </div>
              ))}
              {all.length > 5 && (
                <button data-testid="admin-deposit-ledger-toggle" onClick={() => setShowAllLedger((v) => !v)} className="mt-1 text-xs font-semibold text-primary hover:underline">
                  {showAllLedger ? "Show less" : `Show all ${all.length}`}
                </button>
              )}
            </div>
            );
          })()}
        </div>
      </Section>

      <Section title="Bank details">
        <div className="rounded-xl bg-slate-50 p-3" data-testid="admin-bank-details">
          {u.bank_details ? (
            <div className="space-y-1 text-sm text-slate-700">
              <p data-testid="admin-bank-account-name"><span className="text-slate-400">Account name:</span> <b>{u.bank_details.account_name || "—"}</b></p>
              <p data-testid="admin-bank-iban"><span className="text-slate-400">IBAN:</span> <b className="font-mono">{u.bank_details.iban ? maskIban(u.bank_details.iban) : "—"}</b></p>
              <p data-testid="admin-bank-name"><span className="text-slate-400">Bank:</span> <b>{u.bank_details.bank_name || "—"}</b></p>
            </div>
          ) : (
            <p className="text-sm text-slate-500" data-testid="admin-bank-none">No bank details on file. Seller must add them before a payout or deposit refund can be processed.</p>
          )}
        </div>
      </Section>

      <Section title="Identity verification (KYC)">
        <div className="rounded-xl bg-slate-50 p-4" data-testid="admin-kyc-section">
          <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
            <span className="text-slate-400">Status:</span>
            <span data-testid="admin-kyc-status" className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${KYC_CLS[kyc.status] || KYC_CLS.not_submitted}`}>{KYC_LABEL[kyc.status] || "Not submitted"}</span>
            {kyc.submitted_at && <span className="text-xs text-slate-400">· Submitted {fmtDateTime(kyc.submitted_at)}</span>}
          </div>
          {kyc.status === "rejected" && kyc.rejection_reason && <p className="mb-3 text-sm text-red-600" data-testid="admin-kyc-reason">Reason: {kyc.rejection_reason}</p>}
          {(kyc.front || kyc.back) ? (
            <div className="flex flex-wrap gap-3">
              {kyc.front && <KycThumb label="Front" src={kyc.front} testid="admin-kyc-front" onClick={() => setEnlarge(kyc.front)} />}
              {kyc.back && <KycThumb label="Back" src={kyc.back} testid="admin-kyc-back" onClick={() => setEnlarge(kyc.back)} />}
            </div>
          ) : <p className="text-sm text-slate-500" data-testid="admin-kyc-none">No Emirates ID uploaded yet.</p>}
          {kyc.status === "verified" && u.kyc_verified_name && (
            <p className="mb-2 text-sm text-slate-700" data-testid="admin-kyc-verified-name"><span className="text-slate-400">Verified name (as on Emirates ID):</span> <b>{u.kyc_verified_name}</b></p>
          )}
          {kyc.status !== "verified" && (kyc.front || kyc.back) && (
            <div className="mt-4 space-y-3">
              <div>
                <Label className="mb-1 block text-xs text-slate-600">Full name exactly as printed on the Emirates ID (required to approve)</Label>
                <Input data-testid="kyc-verified-name-input" value={verifyName} onChange={(e) => setVerifyName(e.target.value)} className="max-w-sm rounded-lg" placeholder="e.g. Ahmed Khalid Al Mansoori" />
              </div>
              <ScreenChecks sc={sc} setSc={setSc} notes={scNotes} setNotes={setScNotes} />
              <div className="flex flex-wrap gap-2">
                <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="kyc-approve-btn" disabled={!verifyName.trim() || !(sc.un_scl && sc.uae_ltl && sc.pep_clear && sc.adverse_clear)} onClick={approveKyc}>Approve — no match</Button>
                <Button size="sm" variant="outline" className="rounded-full border-red-300 text-red-600" data-testid="kyc-match-btn" onClick={matchKyc}>Match found</Button>
                <Button size="sm" variant="outline" className="rounded-full text-slate-600" data-testid="kyc-reject-open" onClick={() => { setKycReason(""); setKycReject(true); }}>Reject ID</Button>
              </div>
            </div>
          )}
        </div>
      </Section>

      <Section title="Compliance screening (sanctions / PEP / adverse media)">
        <div className="rounded-xl bg-slate-50 p-4" data-testid="admin-screening-section">
          {u.compliance_blocked && <p className="mb-3 rounded-lg bg-red-100 px-3 py-2 text-sm font-medium text-red-700" data-testid="admin-compliance-blocked">Screening MATCH — payouts & deposit refunds are blocked and the account is suspended (Compliance review).</p>}
          {Array.isArray(u.screenings) && u.screenings.length > 0 ? (
            <ul className="mb-3 space-y-2" data-testid="admin-screening-history">
              {u.screenings.slice().reverse().map((s, i) => (
                <li key={i} className="rounded-lg border border-slate-200 bg-white p-2 text-sm" data-testid="admin-screening-row">
                  <div className="flex items-center justify-between">
                    <span className={s.outcome === "match" ? "font-semibold text-red-600" : "font-semibold text-emerald-700"}>{s.outcome === "match" ? "Match found" : "No match"}</span>
                    <span className="text-xs text-slate-500">{fmtDateTime(s.at)}{s.admin_email ? ` · ${s.admin_email}` : ""}</span>
                  </div>
                  <div className="mt-1 text-xs text-slate-500">UN SCL {s.un_scl ? "✓" : "—"} · UAE LTL {s.uae_ltl ? "✓" : "—"} · PEP {s.pep_clear ? "✓" : "—"} · Adverse media {s.adverse_clear ? "✓" : "—"}{s.notes ? ` · ${s.notes}` : ""}</div>
                </li>
              ))}
            </ul>
          ) : <p className="mb-3 text-sm text-slate-500" data-testid="admin-screening-none">No screening recorded yet.</p>}
          {kyc.status === "verified" && (
            <div className="space-y-3 border-t border-slate-200 pt-3" data-testid="admin-rescreen-controls">
              <p className="text-xs font-medium text-slate-600">Record a new screening{u.rescreen_due ? " — re-screening is due (last check over 90 days ago)" : ""}</p>
              <ScreenChecks sc={sc} setSc={setSc} notes={scNotes} setNotes={setScNotes} />
              <div className="flex flex-wrap gap-2">
                <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="rescreen-clear-btn" disabled={!(sc.un_scl && sc.uae_ltl && sc.pep_clear && sc.adverse_clear)} onClick={() => reScreen("no_match")}>Record — no match</Button>
                <Button size="sm" variant="outline" className="rounded-full border-red-300 text-red-600" data-testid="rescreen-match-btn" onClick={() => reScreen("match")}>Match found</Button>
              </div>
            </div>
          )}
        </div>
      </Section>

      <Section title={`Transactions (${data.transactions.length})`}>
        {data.transactions.length === 0 ? <Empty /> : data.transactions.map((t) => (
          <Line key={t.id} left={t.listing_title} sub={`${t.buyer_id === userId ? "Buyer" : "Seller"} · ${AED(t.agreed_price)}`} right={<Badge className="rounded-full bg-slate-100 text-slate-700">{tLabel(t.status)}</Badge>} />
        ))}
      </Section>

      <Section title={`Disputes (${data.disputes.length})`}>
        {data.disputes.length === 0 ? <Empty /> : data.disputes.map((d) => (
          <Line key={d.id} left={d.listing_title} sub={d.category} right={<Badge className="rounded-full bg-amber-100 text-amber-700">{dLabel(d.status)}</Badge>} />
        ))}
      </Section>

      <Section title="Legal acceptance history">
        <LegalSnapshotDialog version={snapV} onClose={() => setSnapV(null)} />
        <div className="rounded-xl bg-slate-50 p-3" data-testid="admin-terms-history">
          {Array.isArray(u.terms_acceptances) && u.terms_acceptances.length > 0 ? (
            <ul className="space-y-1 text-sm text-slate-700">
              {u.terms_acceptances.slice().reverse().map((a, i) => (
                <li key={i} data-testid="admin-terms-accept-row">Legal v{a.terms_version} · {fmtDateTime(a.accepted_at)} · <button type="button" className="font-semibold text-primary hover:underline" data-testid={`admin-terms-snapshot-${i}`} onClick={() => setSnapV(a.terms_version)}>View what they agreed to</button></li>
              ))}
            </ul>
          ) : <p className="text-sm text-slate-500" data-testid="admin-terms-none">No legal acceptance recorded yet.</p>}
        </div>
      </Section>

      <UserEmails userId={userId} />

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Edit user profile</DialogTitle><DialogDescription>Email cannot be changed. Changes are logged in the admin audit log.</DialogDescription></DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label className="mb-1 block text-xs">Full name</Label><Input data-testid="edit-user-name" value={form.name || ""} onChange={setF("name")} className="rounded-lg" /></div>
            <div><Label className="mb-1 block text-xs">Phone</Label><Input data-testid="edit-user-mobile" value={form.mobile || ""} onChange={setF("mobile")} className="rounded-lg" /></div>
            <div><Label className="mb-1 block text-xs">Emirate</Label><Input data-testid="edit-user-emirate" value={form.emirate || ""} onChange={setF("emirate")} className="rounded-lg" /></div>
            <div><Label className="mb-1 block text-xs">Area</Label><DubaiAreaSelect value={form.area || ""} onChange={(v) => setForm({ ...form, area: v })} testid="edit-user-area" /></div>
            <div className="sm:col-span-2"><Label className="mb-1 block text-xs">Full address</Label><Input data-testid="edit-user-address" value={form.full_address || ""} onChange={setF("full_address")} className="rounded-lg" /></div>
            <div className="sm:col-span-2"><Label className="mb-1 block text-xs">Access instructions</Label><Input data-testid="edit-user-access" value={form.building_access || ""} onChange={setF("building_access")} className="rounded-lg" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button className="rounded-full bg-primary" data-testid="edit-user-save" disabled={saving} onClick={saveEdit}>{saving ? "Saving…" : "Save changes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Confirm: {confirm?.[1]}</DialogTitle><DialogDescription>Are you sure you want to {confirm?.[1].toLowerCase()} {u.name}?</DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setConfirm(null)}>Cancel</Button>
            <Button className="rounded-full bg-primary" data-testid="profile-action-confirm" onClick={doAction}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={kycReject} onOpenChange={setKycReject}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Reject identity verification</DialogTitle><DialogDescription>Enter a reason. The seller will be notified and asked to resubmit.</DialogDescription></DialogHeader>
          <textarea data-testid="kyc-reject-reason" value={kycReason} onChange={(e) => setKycReason(e.target.value)} rows={3} className="w-full rounded-lg border border-slate-200 p-2 text-sm" placeholder="e.g. Photo is blurry / details not legible" />
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setKycReject(false)}>Cancel</Button>
            <Button className="rounded-full bg-red-600 hover:bg-red-700" data-testid="kyc-reject-confirm" disabled={!kycReason.trim()} onClick={rejectKyc}>Confirm rejection</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={banOpen} onOpenChange={(o) => { if (!o) { setBanOpen(false); setBanReason(""); } }}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="text-red-700">Permanently close this account</DialogTitle><DialogDescription>This bans {u.name} entirely — they are logged out, blocked from logging in, and can no longer buy or sell. Any pending order continues so the other party isn't stranded. Reversing a ban later requires a separate confirmation.</DialogDescription></DialogHeader>
          <textarea data-testid="ban-reason" value={banReason} onChange={(e) => setBanReason(e.target.value)} rows={3} className="w-full rounded-lg border border-slate-200 p-2 text-sm" placeholder="Reason for the ban (required — included in the closure email)" />
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => { setBanOpen(false); setBanReason(""); }}>Cancel</Button>
            <Button className="rounded-full bg-red-600 hover:bg-red-700" data-testid="ban-confirm" disabled={!banReason.trim()} onClick={doBan}>Ban permanently</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={unbanOpen} onOpenChange={(o) => { if (!o) { setUnbanOpen(false); setUnbanReason(""); } }}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Reverse ban</DialogTitle><DialogDescription>A ban is a serious decision. To restore {u.name}'s account, enter the reason for the reversal — it is recorded in the admin audit log.</DialogDescription></DialogHeader>
          <textarea data-testid="unban-reason" value={unbanReason} onChange={(e) => setUnbanReason(e.target.value)} rows={3} className="w-full rounded-lg border border-slate-200 p-2 text-sm" placeholder="Reason for reversing the ban (required)" />
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => { setUnbanOpen(false); setUnbanReason(""); }}>Cancel</Button>
            <Button className="rounded-full bg-primary" data-testid="unban-confirm" disabled={!unbanReason.trim()} onClick={doUnban}>Reverse ban</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={suspendOpen} onOpenChange={(o) => { if (!o) { setSuspendOpen(false); setSuspendReason(""); } }}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Suspend this account</DialogTitle><DialogDescription>Enter the reason for suspending {u.name} (required). It is recorded in the admin audit log and the seller is notified.</DialogDescription></DialogHeader>
          <textarea data-testid="suspend-reason" value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)} rows={3} className="w-full rounded-lg border border-slate-200 p-2 text-sm" placeholder="Reason for the suspension (required)" />
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => { setSuspendOpen(false); setSuspendReason(""); }}>Cancel</Button>
            <Button className="rounded-full bg-primary" data-testid="suspend-confirm" disabled={!suspendReason.trim()} onClick={doSuspend}>Suspend account</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={archiveOpen} onOpenChange={(o) => { if (!o) { setArchiveOpen(false); setArchiveReason(""); } }}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Archive this user</DialogTitle><DialogDescription>Archiving {u.name} hides them from the Users list, excludes them from dashboard counts, blocks them from logging in and removes any active listings. All orders, deposits, payouts, Emirates ID, screening and audit records are kept. A reason is required and recorded in the audit log. This is reversible via Restore.</DialogDescription></DialogHeader>
          <textarea data-testid="archive-reason" value={archiveReason} onChange={(e) => setArchiveReason(e.target.value)} rows={3} className="w-full rounded-lg border border-slate-200 p-2 text-sm" placeholder="Reason for archiving (required)" />
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => { setArchiveOpen(false); setArchiveReason(""); }}>Cancel</Button>
            <Button className="rounded-full bg-slate-800 hover:bg-slate-900" data-testid="archive-confirm" disabled={!archiveReason.trim()} onClick={doArchive}>Archive user</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={restoreOpen} onOpenChange={(o) => { if (!o) { setRestoreOpen(false); setRestoreReason(""); } }}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Restore this user</DialogTitle><DialogDescription>Restoring {u.name} returns them to the Users list and lets them log in again. Archived listings come back online only if their deposit is AED 49 or more and they are not suspended or banned. A reason is optional and recorded in the audit log.</DialogDescription></DialogHeader>
          <textarea data-testid="restore-reason" value={restoreReason} onChange={(e) => setRestoreReason(e.target.value)} rows={3} className="w-full rounded-lg border border-slate-200 p-2 text-sm" placeholder="Reason for restoring (optional)" />
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => { setRestoreOpen(false); setRestoreReason(""); }}>Cancel</Button>
            <Button className="rounded-full bg-primary" data-testid="restore-confirm" onClick={doRestore}>Restore user</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!enlarge} onOpenChange={(o) => !o && setEnlarge(null)}>        <DialogContent className="max-w-2xl rounded-2xl">
          <DialogHeader><DialogTitle>Emirates ID</DialogTitle></DialogHeader>
          {enlarge && (enlarge.startsWith("data:application/pdf")
            ? <a href={enlarge} target="_blank" rel="noreferrer" className="text-primary underline">Open PDF in new tab</a>
            : <img src={imgSrc(enlarge)} alt="Emirates ID" data-testid="admin-kyc-enlarged" className="max-h-[70vh] w-full rounded-lg object-contain" />)}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const KycThumb = ({ label, src, testid, onClick }) => {
  const isPdf = typeof src === "string" && src.startsWith("data:application/pdf");
  return (
    <div className="text-center">
      <p className="mb-1 text-xs text-slate-400">{label}</p>
      {isPdf
        ? <a href={src} target="_blank" rel="noreferrer" data-testid={testid} className="flex h-24 w-40 items-center justify-center rounded-lg border border-slate-300 bg-white text-sm text-primary hover:underline">View PDF</a>
        : <img src={imgSrc(src)} alt={label} data-testid={testid} onClick={onClick} className="h-24 w-40 cursor-zoom-in rounded-lg border border-slate-200 object-cover" />}
    </div>
  );
};
const Info = ({ icon: I, label, value }) => (
  <div className="flex items-start gap-2 rounded-xl border border-slate-200 p-3"><I className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" /><div><p className="text-xs text-slate-400">{label}</p><p className="text-sm font-medium text-slate-800">{value}</p></div></div>
);
const Metric = ({ label, value }) => (<div className="rounded-xl border border-slate-200 p-4 text-center"><p className="font-heading text-2xl font-bold text-slate-900">{value}</p><p className="text-xs text-slate-500">{label}</p></div>);
function ScreenChecks({ sc, setSc, notes, setNotes }) {
  const Row = ({ k, children }) => (
    <label className="flex items-start gap-2 text-xs text-slate-700" data-testid={`screen-check-${k}`}>
      <input type="checkbox" checked={!!sc[k]} onChange={(e) => setSc((p) => ({ ...p, [k]: e.target.checked }))} className="mt-0.5 h-4 w-4 shrink-0" data-testid={`screen-check-${k}-input`} />
      <span>{children}</span>
    </label>
  );
  return (
    <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-3" data-testid="screen-checks">
      <Row k="un_scl">Checked against the <a href="https://www.un.org/securitycouncil/content/un-sc-consolidated-list" target="_blank" rel="noreferrer" className="font-medium text-primary underline">UN Security Council Consolidated List</a> — no match</Row>
      <Row k="uae_ltl">Checked against the <a href="https://www.uaeiec.gov.ae/en-us/" target="_blank" rel="noreferrer" className="font-medium text-primary underline">UAE Local Terrorist List</a> — no match</Row>
      <Row k="pep_clear">Checked for politically exposed person status — not a PEP</Row>
      <Row k="adverse_clear">Adverse media search — nothing found</Row>
      <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)" rows={2} className="mt-1 w-full rounded-lg border border-slate-200 p-2 text-xs" data-testid="screen-notes" />
    </div>
  );
}


const Section = ({ title, children }) => (<div className="mt-6"><h3 className="mb-2 font-heading font-semibold text-slate-900">{title}</h3><div className="space-y-2">{children}</div></div>);
const Line = ({ left, sub, right }) => (<div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-3"><div><p className="text-sm font-medium text-slate-900">{left}</p><p className="text-xs text-slate-500">{sub}</p></div>{right}</div>);
const Empty = () => <p className="rounded-xl border border-dashed border-slate-300 p-4 text-center text-sm text-slate-400">Nothing here.</p>;
